import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, BookOpen, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CATEGORIES, CATEGORY_ICON, ROLES, ROLE_LABEL, formatDate } from "@/lib/constants";
import { PageHeader, RoleBadge, StatusBadge, EmptyState } from "@/components/Badges";

const ALL = "all";

const Chip = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
      active ? "border-lasso-ink bg-lasso-ink text-white" : "border-lasso-line bg-white text-zinc-600 hover:border-lasso-ink hover:text-lasso-ink"
    }`}
  >
    {children}
  </button>
);

const SopCard = ({ sop, isAdmin }) => {
  const Icon = CATEGORY_ICON[sop.category] || BookOpen;
  return (
    <Link
      to={`/sops/${sop.id}`}
      data-testid={`sop-card-item-${sop.id}`}
      className="group flex flex-col rounded-2xl border border-lasso-line bg-white p-6 transition-[transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-lasso-ink"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-lasso-deep"><Icon className="h-4 w-4" /> {sop.category}</span>
        {isAdmin ? <StatusBadge status={sop.status} testId={`sop-card-status-${sop.id}`} /> : <ArrowUpRight className="h-4 w-4 text-zinc-400 group-hover:text-lasso-ink" />}
      </div>
      <h3 className="mt-5 font-display text-base font-bold leading-snug">{sop.title}</h3>
      <p className="mt-2 line-clamp-2 text-sm text-zinc-600">{sop.summary}</p>
      <div className="mt-5 flex flex-wrap gap-1.5">
        {sop.applicable_roles.map((r) => <RoleBadge key={r} role={r} />)}
      </div>
      <div className="mt-auto flex items-center gap-3 pt-6 text-xs text-muted-foreground">
        <span className="font-mono">v{sop.version}</span>
        <span>·</span>
        <span>{sop.step_count} steps</span>
        <span>·</span>
        <span>Updated {formatDate(sop.updated_at)}</span>
      </div>
    </Link>
  );
};

export default function SopLibrary() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(ALL);
  const [role, setRole] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [sops, setSops] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (category !== ALL) params.category = category;
      if (role !== ALL) params.role = role;
      if (isAdmin && status !== ALL) params.status = status;
      api.get("/sops", { params }).then((r) => setSops(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [q, category, role, status, isAdmin]);

  return (
    <div className="fade-up">
      <PageHeader
        eyebrow="SOP Library"
        title="Standard operating procedures"
        description="The current, approved way we do things at every Lasso community."
        actions={(isAdmin || user.is_region_admin) && (
          <Link to="/sops/new" data-testid="sop-new-button" className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-lasso-navy">
            <Plus className="h-4 w-4" /> New SOP
          </Link>
        )}
      />

      <div className="mb-6 flex flex-col gap-3 md:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles, steps and details…" className="h-12 rounded-full bg-white pl-11" data-testid="sop-search-input" />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="h-12 rounded-full bg-white md:w-56" data-testid="sop-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL} data-testid="sop-role-filter-all">All roles</SelectItem>
            <SelectItem value={user.role} data-testid="sop-role-filter-mine">My role ({ROLE_LABEL[user.role]})</SelectItem>
            {ROLES.filter((r) => r.value !== user.role).map((r) => (
              <SelectItem key={r.value} value={r.value} data-testid={`sop-role-filter-${r.value}`}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isAdmin && (
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-12 rounded-full bg-white md:w-44" data-testid="sop-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[ALL, "published", "draft", "archived"].map((s) => (
                <SelectItem key={s} value={s} className="capitalize" data-testid={`sop-status-filter-${s}`}>{s === ALL ? "All statuses" : s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="mb-10 flex flex-wrap gap-2" data-testid="sop-category-filter">
        <Chip active={category === ALL} onClick={() => setCategory(ALL)} testId="sop-category-all">All categories</Chip>
        {CATEGORIES.map((c) => (
          <Chip key={c.value} active={category === c.value} onClick={() => setCategory(c.value)} testId={`sop-category-${c.value.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            {c.value}
          </Chip>
        ))}
      </div>

      {sops && (
        <p className="mb-4 text-xs uppercase tracking-[0.16em] text-muted-foreground" data-testid="sop-result-count">{sops.length} {sops.length === 1 ? "SOP" : "SOPs"}</p>
      )}
      {sops && sops.length === 0 && <EmptyState title="No SOPs match your filters" description="Try a different search term or category." testId="sop-empty-state" />}
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {sops?.map((s) => <SopCard key={s.id} sop={s} isAdmin={isAdmin} />)}
      </div>
    </div>
  );
}
